
import React from "react";
import { Link } from "react-router-dom";
import { 
  ArrowRight, Check, Clock, Bookmark, 
  ThumbsUp, ThumbsDown, MessageCircle,
  Shield, FileCheck, Building, Gavel, Lock
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";

interface ComplianceContentProps {
  activePage: string;
  feedbackSubmitted: Record<string, boolean>;
  bookmarkedArticles: string[];
  submitFeedback: (articleId: string, isHelpful: boolean) => void;
  toggleBookmark: (articleId: string) => void;
}

export const ComplianceContent: React.FC<ComplianceContentProps> = ({
  activePage,
  feedbackSubmitted,
  bookmarkedArticles,
  submitFeedback,
  toggleBookmark,
}) => {
  if (activePage === "compliance") {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Compliance</h1>
          <p className="text-gray-500 mt-2">
            Learn about our compliance with industry standards and regulations.
          </p>
          <div className="flex items-center mt-2 text-sm text-gray-500">
            <Clock className="h-4 w-4 mr-1" />
            <span>Last updated: August 15, 2023</span>
            <Badge variant="outline" className="ml-2">Enterprise</Badge>
          </div>
        </div>

        <div className="prose max-w-none">
          <h2 id="compliance-overview">Compliance Overview</h2>
          <p>
            DGK Platform is committed to maintaining compliance with applicable laws, regulations, and industry standards. 
            Our comprehensive compliance program ensures that we meet or exceed requirements for data protection, privacy, 
            security, and business operations.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 my-6">
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <FileCheck className="h-5 w-5 text-blue-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium">Certifications</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Industry standard certifications and audits
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3">
                    <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                      <Building className="h-5 w-5 text-green-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium">Governance</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Policies, procedures, and oversight framework
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-l-4 border-l-purple-500">
              <CardContent className="pt-6">
                <div className="flex items-start">
                  <div className="flex-shrink-0 mr-3">
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                      <Gavel className="h-5 w-5 text-purple-600" />
                    </div>
                  </div>
                  <div>
                    <h3 className="font-medium">Legal</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Regulatory compliance across jurisdictions
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <h2 id="certifications">Certifications & Standards</h2>
          
          <p>
            Our platform maintains the following certifications and complies with international standards:
          </p>
          
          <div className="space-y-4 my-6">
            <div className="flex items-start p-4 bg-white border rounded-lg shadow-sm">
              <div className="flex-shrink-0 mr-4">
                <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                  <Shield className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-1">SOC 2 Type II</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Service Organization Control report focusing on security, availability, processing integrity, 
                  confidentiality, and privacy controls.
                </p>
                <Badge variant="outline" className="text-blue-600 bg-blue-50 border-blue-200">Verified Annually</Badge>
              </div>
            </div>
            
            <div className="flex items-start p-4 bg-white border rounded-lg shadow-sm">
              <div className="flex-shrink-0 mr-4">
                <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                  <Lock className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-1">GDPR Compliance</h3>
                <p className="text-sm text-gray-600 mb-2">
                  Full compliance with the European Union's General Data Protection Regulation for data privacy and protection.
                </p>
                <Badge variant="outline" className="text-green-600 bg-green-50 border-green-200">Continuous Compliance</Badge>
              </div>
            </div>
            
            <div className="flex items-start p-4 bg-white border rounded-lg shadow-sm">
              <div className="flex-shrink-0 mr-4">
                <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center">
                  <Building className="h-6 w-6 text-indigo-600" />
                </div>
              </div>
              <div>
                <h3 className="text-lg font-medium mb-1">ISO 27001</h3>
                <p className="text-sm text-gray-600 mb-2">
                  International standard for information security management systems (ISMS).
                </p>
                <Badge variant="outline" className="text-indigo-600 bg-indigo-50 border-indigo-200">Certified</Badge>
              </div>
            </div>
          </div>

          <h2 id="data-regulations">Data Regulations Compliance</h2>
          
          <Alert className="my-6 bg-amber-50 border-amber-200">
            <div className="flex items-center">
              <Gavel className="h-5 w-5 text-amber-600 mr-2" />
              <AlertTitle className="text-amber-800">Regional Compliance</AlertTitle>
            </div>
            <AlertDescription className="text-amber-700 mt-2">
              Our platform complies with regional data protection and privacy regulations worldwide. The following 
              highlights key regulations that impact our services.
            </AlertDescription>
          </Alert>
          
          <Accordion type="single" collapsible className="w-full my-6">
            <AccordionItem value="gdpr">
              <AccordionTrigger className="text-base font-medium">
                GDPR (European Union)
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                <p className="mt-2">
                  We maintain full compliance with the General Data Protection Regulation (GDPR) for EU citizens' data.
                </p>
                <div className="bg-gray-50 p-3 rounded mt-3 space-y-2 text-sm">
                  <div className="flex items-start">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 mr-2" />
                    <p>Data Processing Agreements (DPAs) available for all customers</p>
                  </div>
                  <div className="flex items-start">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 mr-2" />
                    <p>EU-approved Standard Contractual Clauses (SCCs) for data transfers</p>
                  </div>
                  <div className="flex items-start">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 mr-2" />
                    <p>Data Subject Access Request (DSAR) handling procedures</p>
                  </div>
                  <div className="flex items-start">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 mr-2" />
                    <p>Data minimization and retention policies</p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="ccpa">
              <AccordionTrigger className="text-base font-medium">
                CCPA/CPRA (California, USA)
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                <p className="mt-2">
                  We comply with the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA).
                </p>
                <div className="bg-gray-50 p-3 rounded mt-3 space-y-2 text-sm">
                  <div className="flex items-start">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 mr-2" />
                    <p>Notice at collection provided to California residents</p>
                  </div>
                  <div className="flex items-start">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 mr-2" />
                    <p>Right to know/access, delete, and opt-out mechanisms</p>
                  </div>
                  <div className="flex items-start">
                    <Check className="h-4 w-4 text-green-500 mt-0.5 mr-2" />
                    <p>No sale of personal information without explicit consent</p>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            
            <AccordionItem value="pipeda">
              <AccordionTrigger className="text-base font-medium">
                PIPEDA (Canada)
              </AccordionTrigger>
              <AccordionContent className="text-gray-600">
                <p className="mt-2">
                  We adhere to Canada's Personal Information Protection and Electronic Documents Act (PIPEDA).
                </p>
                <div className="bg-gray-50 p-3 rounded mt-3 text-sm">
                  <p>
                    Our platform follows PIPEDA's fair information principles, including accountability, identifying purposes, 
                    consent, limiting collection and use, disclosure, retention, accuracy, safeguards, openness, individual 
                    access, and challenging compliance.
                  </p>
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>

          <h2 id="enterprise-compliance">Enterprise Compliance Support</h2>
          <p>
            For enterprise customers, we offer enhanced compliance support and documentation:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium mb-2">Custom Compliance Reports</h3>
                <p className="text-sm text-gray-600">
                  We can provide customized compliance reports specific to your industry or regulatory requirements.
                </p>
                <Button variant="link" className="px-0 mt-2 text-blue-600 hover:text-blue-800">
                  Contact Enterprise Sales <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="pt-6">
                <h3 className="font-medium mb-2">Vendor Risk Assessment</h3>
                <p className="text-sm text-gray-600">
                  Full documentation for your vendor risk management and third-party risk assessment processes.
                </p>
                <Button variant="link" className="px-0 mt-2 text-blue-600 hover:text-blue-800">
                  Request Documentation <ArrowRight className="h-4 w-4 ml-1" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Feedback section */}
        <div className="bg-gray-50 p-6 rounded-lg border border-gray-200 mt-10">
          <h3 className="font-medium mb-3">Was this article helpful?</h3>
          {!feedbackSubmitted["compliance"] ? (
            <div className="flex space-x-4">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("compliance", true)}
                className="flex items-center"
              >
                <ThumbsUp className="h-4 w-4 mr-2" />
                Yes, it helped
              </Button>
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => submitFeedback("compliance", false)}
                className="flex items-center"
              >
                <ThumbsDown className="h-4 w-4 mr-2" />
                No, I need more info
              </Button>
            </div>
          ) : (
            <div className="text-green-600 flex items-center">
              <Check className="h-5 w-5 mr-2" />
              Thank you for your feedback!
            </div>
          )}
          <div className="mt-4 flex items-center text-sm text-gray-500">
            <MessageCircle className="h-4 w-4 mr-2" />
            <Link to="#" className="text-blue-600 hover:underline">
              Ask a question
            </Link>
            <span className="mx-2">·</span>
            <Bookmark 
              className={`h-4 w-4 mr-2 ${bookmarkedArticles.includes("compliance") ? "fill-blue-600 text-blue-600" : ""}`} 
            />
            <Button 
              variant="link" 
              className="p-0 h-auto text-sm"
              onClick={() => toggleBookmark("compliance")}
            >
              {bookmarkedArticles.includes("compliance") ? "Bookmarked" : "Bookmark"}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return null;
};
